import { UserActionTypes } from '@/store/modules/users/actions'
import { UserMutationTypes } from '@/store/modules/users/mutations'
import { AxiosResponse } from 'axios'
import _find from 'lodash.find'
import differenceBy from 'lodash.differenceby'
import { ActionContext, GetterTree, MutationTree } from 'vuex'
import companiesApiService from '@/services/companies'
import { Company, CompanyId, CompanySubscription } from '@/models/companies'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import { RootState } from '../state'
import { localStorageSet } from '@/helpers/localStorage'
import {
  setCarrotQuestCompanyParams,
  sendAnalyticEvent,
  AnalyticsEvent,
} from '@/helpers/analytics'
import i18n from '@/i18n'
import { DataTablePayload } from '@/models/dataTable'
import { DataTableResponse } from '@/models/scripts'

// Types definitions

type CompaniesContext = ActionContext<CompaniesState, RootState>
export interface CompaniesState {
  list: Company[]
  subscription: CompanySubscription | undefined
  orders: DataTableResponse
}

// Store module

function initialState(): CompaniesState {
  return {
    list: [],
    subscription: undefined,
    orders: {
      data: [],
      filter: {},
      sort: {},
      pagination: {
        offset: 0,
        limit: 20,
        total: 0,
      },
      columns: [],
    },
  }
}

const state: CompaniesState = initialState()

const getters: GetterTree<CompaniesState, unknown> = {
  getCompaniesList: state => state.list,

  getActiveCompany: (state, rootGetters): Company | undefined => {
    const active: string = rootGetters.getMe.company
    if (active !== '') {
      return _find(state.list, ['id', active])
    }
  },

  getCompanyIsPro: (state, rootGetters): boolean => {
    return !!rootGetters.getActiveCompany?.subscription
  },

  getCompanySubscription: state => state.subscription,

  getOrders: state => state.orders,
}

export enum CompanyActionTypes {
  Fetch = '[Company Action] Fetch', // COMPANIES__FETCH
  FetchAndActivateNew = '[Company Action] Fetch and activate new', // COMPANIES__FETCH
  Create = '[Company Action] Create', // COMPANIES__CREATE
  Activate = '[Company Action] Activate', // COMPANIES__ACTIVE
  Update = '[Company Action] Update',
  Delete = '[Company Action] Delete',
  Subscription = '[Company Action] Subscription',
  Orders = '[Company Action] Orders',
}

export enum CompanyMutationTypes {
  Save = '[Company Mutation] Save', // _COMPANIES__SAVE
  SubscriptionSuccess = '[Company Mutation] Subscription Success',
  OrdersSuccess = '[Company Mutation] Orders Success',
}

const actions = {
  /**
   * Fetch all user's companies
   *
   * @param {CompaniesContext} context
   * @returns {Promise<AxiosResponse<Company[]>>}
   */
  async [CompanyActionTypes.Fetch](
    context: CompaniesContext,
  ): Promise<AxiosResponse<Company[]>> {
    const response = await companiesApiService.get()
    const { company } = context.rootGetters.getMe
    const companyIsExist =
      !!company && response.data.some(d => d.id === company)

    if (!companyIsExist && response.data?.length > 0) {
      // Tempfix! TODO: need fix BE. Today /companies/:id/use api required X-Company-id header.
      localStorageSet('company', `${response.data[0].id}`)
      await context.dispatch(CompanyActionTypes.Activate, response.data[0].id)
    }
    context.commit(CompanyMutationTypes.Save, response.data)
    setCarrotQuestCompanyParams(response.data)
    return response
  },

  async [CompanyActionTypes.FetchAndActivateNew]({
    getters,
    dispatch,
  }: CompaniesContext): Promise<void> {
    const oldCompanies = [...getters.getCompaniesList]

    await dispatch(UserActionTypes.GetCurrentUser)
    await dispatch(CompanyActionTypes.Fetch)

    const newCompany = differenceBy(
      getters.getCompaniesList,
      oldCompanies,
      (company: Company) => company.id,
    )[0]

    if (newCompany) {
      await dispatch(CompanyActionTypes.Activate, newCompany.id)
    }
  },

  /**
   * Create company request
   *
   * @param {CompaniesContext} { commit, dispatch }
   * @param {string} title
   */
  async [CompanyActionTypes.Create](
    { dispatch, getters, rootGetters }: CompaniesContext,
    title: string,
  ): Promise<AxiosResponse<CompanyId>> {
    const response = await companiesApiService.create(title)
    await dispatch(UserActionTypes.GetCurrentUser)
    await dispatch(CompanyActionTypes.Fetch)
    await dispatch(CompanyActionTypes.Activate, response.data.id)

    const isFirstCompanyCreated = getters.getCompaniesList?.length === 1

    if (isFirstCompanyCreated) {
      sendAnalyticEvent(AnalyticsEvent.REGISTER_SUCCESS, {
        user: rootGetters.getMe,
      })
    }

    return response
  },

  /**
   * Update company request
   *
   */
  async [CompanyActionTypes.Update](
    { dispatch, getters }: CompaniesContext,
    title: string,
  ): Promise<AxiosResponse<boolean>> {
    const activeUserCompanyId: string = getters.getMe.company.toString()
    const response = await companiesApiService.update(
      activeUserCompanyId,
      title,
    )

    if (response.data) {
      dispatch(ADD_TOAST_MESSAGE, {
        text: i18n.tc('company.updateSuccess'),
        type: 'info',
      })

      await dispatch(UserActionTypes.GetCurrentUser)
      await dispatch(CompanyActionTypes.Fetch)
    }

    return response
  },

  /**
   * Update company delete
   *
   */
  async [CompanyActionTypes.Delete]({
    dispatch,
    getters,
  }: CompaniesContext): Promise<AxiosResponse<boolean>> {
    const activeUserCompanyId: string = getters.getMe.company.toString()
    const response = await companiesApiService.delete(activeUserCompanyId)

    dispatch(ADD_TOAST_MESSAGE, {
      text: i18n.tc('company.archiveSuccess'),
      type: 'info',
    })

    await dispatch(UserActionTypes.GetCurrentUser)
    await dispatch(CompanyActionTypes.Fetch)

    return response
  },

  /**
   * Set active user company and update data after changing
   *
   * @param {CompaniesContext} { commit }
   * @param {string} id
   * @returns {Promise<AxiosResponse<boolean>>}
   */
  async [CompanyActionTypes.Activate](
    { commit }: CompaniesContext,
    id: string,
  ): Promise<AxiosResponse<boolean>> {
    const response = await companiesApiService.activate(id)
    commit(UserMutationTypes.SetActiveCompany, id)
    return response
  },

  async [CompanyActionTypes.Subscription](
    { commit }: CompaniesContext,
    id: string,
  ): Promise<AxiosResponse<CompanySubscription> | undefined> {
    let response
    try {
      response = await companiesApiService.subscription(id)
      commit(CompanyMutationTypes.SubscriptionSuccess, response.data)
    } catch (e) {
      commit(CompanyMutationTypes.SubscriptionSuccess, response)
    }

    return response
  },

  async [CompanyActionTypes.Orders](
    { commit, getters }: CompaniesContext,
    data: DataTablePayload,
  ) {
    const { limit, offset, expand, sort, filter } = data
    const companyId = getters.getActiveCompany.id
    const response = await companiesApiService.getOrders(
      limit,
      offset,
      expand,
      sort,
      filter,
      companyId,
    )
    commit(CompanyMutationTypes.OrdersSuccess, response.data)

    return response
  },
}

const mutations: MutationTree<CompaniesState> = {
  /**
   * Persist companies list for current user
   *
   * @param {CompaniesState} state
   * @param {Company[]} list
   */
  [CompanyMutationTypes.Save](state: CompaniesState, list: Company[]): void {
    state.list = list
  },

  [CompanyMutationTypes.SubscriptionSuccess](
    state: CompaniesState,
    subscription: CompanySubscription,
  ): void {
    state.subscription = subscription
  },

  [CompanyMutationTypes.OrdersSuccess](
    state: CompaniesState,
    orders: DataTableResponse,
  ): void {
    state.orders = orders
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
